// Multiview — CO-160a standard layout.
//
// The definitive 8-tile multiview rendered for every seat in every camera
// mode (IC Manila and Standard Assembly). Layout is fixed and burnt into
// the switcher configuration; operators cannot remap tiles. This component
// owns the visual rendering only — tally state and source assignments are
// passed in from the scenario engine.
//
// Geometry, tile resolution, and label helpers live in
// `multiview-layout.ts` (pure `.ts`, unit-testable).
//
// Fidelity contract: docs/insights/multiview-visual-contract.md
// One master scene is cropped per source so all camera tiles share the
// same physical stage at the same moment.

import { cropForSource, type CameraFraming } from "./scenario-camera-state";
import SceneCrop from "./scene/SceneCrop";
import { useScenario } from "./scene/ScenarioContext";
import type { CropRect } from "./scene/types";
import {
  DEFAULT_MULTIVIEW_LABELS,
  multiviewLabelFor,
  tallyBoxFor,
  tileForSource,
  TILE_BOXES,
  type MultiviewBusSource,
  type MultiviewSourceId,
  type MultiviewSources,
} from "./multiview-layout";

export type {
  MultiviewBusSource,
  MultiviewSourceId,
  MultiviewSources,
} from "./multiview-layout";
export { multiviewLabelFor } from "./multiview-layout";

/**
 * In-flight transition snapshot. When non-null, the big PROGRAM tile
 * crossfades between `fromSource` and `toSource` per `progress` (0..1).
 * The driver decides whether the panel ticks progress (AUTO) or the
 * operator drags the T-bar (manual).
 */
export interface MultiviewTransition {
  fromSource: MultiviewBusSource;
  toSource: MultiviewBusSource;
  progress: number;
  type: "diss" | "wipe" | "dve";
}

export interface MultiviewCO160aProps {
  /** Source currently on PROGRAM (red tally). */
  pgmSource: MultiviewBusSource;
  /** Source currently armed on PREVIEW (green tally). */
  pvwSource: MultiviewBusSource;
  /** Optional label overrides per source tile. */
  sources?: MultiviewSources;
  /** Visual size hint — `compact` (~480px), `regular` (~640px), `wide` (~800px). */
  size?: "compact" | "regular" | "wide";
  /** Optional accessible label override. */
  ariaLabel?: string;
  /** Optional className applied to the outer wrapper. */
  className?: string;
  /**
   * Optional in-flight transition. When set, the big PGM tile composites
   * fromSource over toSource per progress; the small-tile red tally
   * snaps to the destination at the midpoint so the operator can see
   * "where it's going" while the take is rolling.
   */
  transition?: MultiviewTransition | null;
  /** Optional live framing per camera. Defaults to the canonical interview scene. */
  cameraFraming?: Partial<CameraFraming>;
  /**
   * Optional controlled-camera annotation. When set, an amber border +
   * inner framing rectangle are drawn on the named tile. Used by the PTZ
   * controller to show which camera the joystick is currently driving
   * and where its virtual framing sits within the field of view.
   *
   * `framing` is normalised 0..1 over the tile box (x/y are top-left of
   * the framing rect, width/height are fractions of the tile). Defaults
   * to the centred 60% × 50% rectangle if omitted.
   */
  controllingSource?: MultiviewSourceId | null;
  controllingFraming?: { x: number; y: number; width: number; height: number };
}

const SIZE_PX: Record<NonNullable<MultiviewCO160aProps["size"]>, number> = {
  compact: 480,
  regular: 640,
  wide: 800,
};

/**
 * The CO-160a multiview component. Renders a single SVG that mirrors the
 * canonical reference at any size. Tally borders are computed from
 * `pgmSource` / `pvwSource`.
 */
export default function MultiviewCO160a({
  pgmSource,
  pvwSource,
  sources,
  size = "regular",
  ariaLabel,
  className,
  transition = null,
  cameraFraming,
  controllingSource = null,
  controllingFraming,
}: MultiviewCO160aProps) {
  const { scenario } = useScenario();
  const master = scenario.master;
  const sceneDescNoun = `${scenario.label.toLowerCase()} master scene`;
  const labels = { ...DEFAULT_MULTIVIEW_LABELS, ...sources };
  const pgmTile = tileForSource(pgmSource);
  const pvwTile = tileForSource(pvwSource);

  const widthPx = SIZE_PX[size];

  // Once the transition crosses 50% the destination is what the audience
  // is seeing — snap the red tally over to it so multiview state matches
  // air. Below 50%, tally still belongs to the outgoing source.
  const effectivePgmTile =
    transition && transition.progress >= 0.5
      ? tileForSource(transition.toSource) ?? pgmTile
      : pgmTile;

  // Pre-compute tally boxes for the small tiles (when applicable).
  const pgmTallyBox = effectivePgmTile ? tallyBoxFor(effectivePgmTile) : null;
  const pvwTallyBox = pvwTile ? tallyBoxFor(pvwTile) : null;

  // Controlled-camera framing overlay (PTZ seat).
  const controllingTileBox = controllingSource ? TILE_BOXES[controllingSource] : null;
  const framing = controllingFraming ?? { x: 0.2, y: 0.25, width: 0.6, height: 0.5 };

  const sourceCrop = (source: MultiviewBusSource): CropRect | null =>
    cropForSource(source, cameraFraming);
  const programCrop = sourceCrop(pgmSource);
  const previewCrop = sourceCrop(pvwSource);
  const transitionFromCrop = transition ? sourceCrop(transition.fromSource) : null;
  const transitionToCrop = transition ? sourceCrop(transition.toSource) : null;

  const renderSourceLabel = (label: string, box: { x: number; y: number; width: number; height: number }) => (
    <g aria-hidden="true">
      <rect
        className="mv-label-pill"
        x={box.x + 6}
        y={box.y + box.height - 22}
        width={Math.min(82, Math.max(58, label.length * 7))}
        height={16}
        rx={3}
      />
      <text className="mv-label-overlay" x={box.x + 12} y={box.y + box.height - 10}>
        {label}
      </text>
    </g>
  );

  const renderCameraTile = (source: "cam1" | "cam2" | "cam3") => {
    const box = TILE_BOXES[source];
    const crop = sourceCrop(source);
    return (
      <>
        <rect className="mv-tile mv-tile-photo-bg" x={box.x} y={box.y} width={box.width} height={box.height} />
        {crop && (
          <SceneCrop
            master={master}
            crop={crop}
            targetBox={box}
            title={labels[source]}
            desc={`${labels[source]} crop of the ${sceneDescNoun}.`}
          />
        )}
        {renderSourceLabel(labels[source], box)}
      </>
    );
  };

  const renderBigTile = (
    kind: "preview" | "program",
    box: typeof TILE_BOXES.previewBig,
    crop: CropRect | null,
    source: MultiviewBusSource,
  ) => (
    <>
      <rect
        className={`mv-tile ${kind === "preview" ? "mv-tile-big-pvw" : "mv-tile-big-pgm"}`}
        x={box.x}
        y={box.y}
        width={box.width}
        height={box.height}
      />
      {crop && (
        <SceneCrop
          master={master}
          crop={crop}
          targetBox={box}
          title={kind === "preview" ? "Preview source" : "Program source"}
          desc={`${multiviewLabelFor(source, sources)} crop of the ${sceneDescNoun}.`}
        />
      )}
      <text className="mv-label mv-label-corner" x={box.x + 10} y={box.y + 18}>
        {kind === "preview" ? "PREVIEW" : "PROGRAM"}
      </text>
      {renderSourceLabel(multiviewLabelFor(source, sources), box)}
    </>
  );

  const wipeClipWidth = TILE_BOXES.programBig.width * (transition?.progress ?? 0);
  const dveProgress = Math.max(0.02, transition?.progress ?? 0);
  const dveCx = TILE_BOXES.programBig.x + TILE_BOXES.programBig.width;
  const dveCy = TILE_BOXES.programBig.y + TILE_BOXES.programBig.height;

  return (
    <div
      className={className}
      style={{ width: "100%", maxWidth: widthPx, margin: "0 auto" }}
      data-multiview="co-160a"
      data-pgm={String(pgmSource)}
      data-pvw={String(pvwSource)}
    >
      <svg
        viewBox="0 0 640 480"
        role="img"
        aria-label={
          ariaLabel ??
          `Multiview — PROGRAM ${multiviewLabelFor(pgmSource, sources)}, PREVIEW ${multiviewLabelFor(pvwSource, sources)}`
        }
        style={{ width: "100%", height: "auto", display: "block" }}
      >
        <defs>
          <style>{`
            .mv-tv-body  { fill: #d8dade; stroke: #8e9196; stroke-width: 2; stroke-linejoin: round; }
            .mv-tv-bezel { fill: #5d6066; }
            .mv-tv-stand { fill: #5d6066; stroke: none; }
            .mv-tv-base  { fill: #3f4247; stroke: none; }
            .mv-tile     { fill: #ffffff; stroke: none; }
            .mv-tile-dim { fill: #a4a7ac; stroke: none; }
            .mv-tally-red   { fill: none; stroke: #e63946; stroke-width: 4; stroke-linejoin: miter; }
            .mv-tally-green { fill: none; stroke: #4aa84b; stroke-width: 4; stroke-linejoin: miter; }
            .mv-label    { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
                           fill: #3a3d41; font-weight: 500; text-anchor: middle; dominant-baseline: middle; }
            .mv-label-corner { font-size: 10px; letter-spacing: 0; fill: rgba(255,255,255,0.78);
                               font-weight: 700; text-anchor: start; dominant-baseline: central; }
            .mv-label-tile  { font-size: 16px; letter-spacing: 0.5px; }
            .mv-label-dim   { fill: #6c7075; }
            .mv-label-dim-on-dark { fill: #e2e4e8; }
            .mv-label-pill { fill: rgba(7, 9, 13, 0.66); stroke: rgba(255,255,255,0.22); stroke-width: 0.5; }
            .mv-label-overlay { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
                                fill: #ffffff; font-size: 10px; font-weight: 700; letter-spacing: 0;
                                text-anchor: start; dominant-baseline: central; }
            .mv-tile-photo-bg { fill: #16191f; }
            .mv-tile-media { fill: url(#mv-grad-media); }
            .mv-tile-big-pvw { fill: url(#mv-grad-big-pvw); }
            .mv-tile-big-pgm { fill: url(#mv-grad-big-pgm); }
          `}</style>
          <linearGradient id="mv-grad-media" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fbf2e6" />
            <stop offset="100%" stopColor="#f3d6a6" />
          </linearGradient>
          <linearGradient id="mv-grad-big-pvw" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f4f7fb" />
            <stop offset="100%" stopColor="#cfdbe9" />
          </linearGradient>
          <linearGradient id="mv-grad-big-pgm" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fdf3f3" />
            <stop offset="100%" stopColor="#eccfcf" />
          </linearGradient>
          <clipPath id="mv-program-wipe">
            <rect
              x={TILE_BOXES.programBig.x}
              y={TILE_BOXES.programBig.y}
              width={wipeClipWidth}
              height={TILE_BOXES.programBig.height}
            />
          </clipPath>
        </defs>

        {/* TV body / housing */}
        <rect className="mv-tv-body" x={20} y={20} width={600} height={380} rx={8} ry={8} />
        {/* Stand */}
        <path className="mv-tv-stand" d="M 268 400 L 372 400 L 392 438 L 248 438 Z" />
        <rect className="mv-tv-base" x={220} y={436} width={200} height={6} rx={2} />
        {/* Inner bezel */}
        <rect className="mv-tv-bezel" x={36} y={36} width={568} height={348} rx={2} ry={2} />

        {/* === Big tiles (top half) === */}
        {renderBigTile("preview", TILE_BOXES.previewBig, previewCrop, pvwSource)}
        {/* Green tally on PREVIEW big tile */}
        <rect
          className="mv-tally-green"
          x={TILE_BOXES.previewBig.x + 2}
          y={TILE_BOXES.previewBig.y + 2}
          width={TILE_BOXES.previewBig.width - 4}
          height={TILE_BOXES.previewBig.height - 4}
        />

        {transition && transitionFromCrop && transitionToCrop ? (
          <>
            <rect
              className="mv-tile mv-tile-big-pgm"
              x={TILE_BOXES.programBig.x}
              y={TILE_BOXES.programBig.y}
              width={TILE_BOXES.programBig.width}
              height={TILE_BOXES.programBig.height}
            />
            <SceneCrop
              master={master}
              crop={transitionFromCrop}
              targetBox={TILE_BOXES.programBig}
              opacity={transition.type === "diss" ? 1 - transition.progress : 1}
              title="Outgoing program source"
            />
            <SceneCrop
              master={master}
              crop={transitionToCrop}
              targetBox={TILE_BOXES.programBig}
              opacity={transition.type === "diss" ? transition.progress : 1}
              clipPathId={transition.type === "wipe" ? "mv-program-wipe" : undefined}
              transform={
                transition.type === "dve"
                  ? `translate(${dveCx} ${dveCy}) scale(${dveProgress}) translate(${-dveCx} ${-dveCy})`
                  : undefined
              }
              title="Incoming program source"
            />
            <text className="mv-label mv-label-corner" x={TILE_BOXES.programBig.x + 10} y={TILE_BOXES.programBig.y + 18}>
              PROGRAM
            </text>
            {renderSourceLabel(multiviewLabelFor(transition.toSource, sources), TILE_BOXES.programBig)}
          </>
        ) : (
          renderBigTile("program", TILE_BOXES.programBig, programCrop, pgmSource)
        )}
        {/* Red tally on PROGRAM big tile */}
        <rect
          className="mv-tally-red"
          x={TILE_BOXES.programBig.x + 2}
          y={TILE_BOXES.programBig.y + 2}
          width={TILE_BOXES.programBig.width - 4}
          height={TILE_BOXES.programBig.height - 4}
        />

        {/* === Middle row of small tiles (Cameras) === */}
        {/* Tile 1: UNUSED (white bg with gray label) */}
        <rect className="mv-tile" x={48} y={226} width={130} height={76} />
        <text className="mv-label mv-label-tile mv-label-dim" x={113} y={266}>
          UNUSED
        </text>

        {/* Tile 2: CAM 1 */}
        {renderCameraTile("cam1")}

        {/* Tile 3: CAM 2 */}
        {renderCameraTile("cam2")}

        {/* Tile 4: CAM 3 */}
        {renderCameraTile("cam3")}

        {/* === Bottom row of small tiles (Media + UNUSED dims) === */}
        <rect
          className="mv-tile mv-tile-media"
          x={TILE_BOXES.media1.x}
          y={TILE_BOXES.media1.y}
          width={TILE_BOXES.media1.width}
          height={TILE_BOXES.media1.height}
        />
        <text className="mv-label mv-label-tile" x={113} y={348}>
          {labels.media1}
        </text>

        <rect
          className="mv-tile mv-tile-media"
          x={TILE_BOXES.media2.x}
          y={TILE_BOXES.media2.y}
          width={TILE_BOXES.media2.width}
          height={TILE_BOXES.media2.height}
        />
        <text className="mv-label mv-label-tile" x={251} y={348}>
          {labels.media2}
        </text>

        {/* Tile 3 + 4: UNUSED (dim) */}
        <rect className="mv-tile-dim" x={324} y={312} width={130} height={68} />
        <text className="mv-label mv-label-tile mv-label-dim-on-dark" x={389} y={348}>
          UNUSED
        </text>
        <rect className="mv-tile-dim" x={462} y={312} width={130} height={68} />
        <text className="mv-label mv-label-tile mv-label-dim-on-dark" x={527} y={348}>
          UNUSED
        </text>

        {/* === Tally rings on small tiles, drawn last so they sit on top === */}
        {pvwTallyBox && (
          <rect
            className="mv-tally-green"
            x={pvwTallyBox.x}
            y={pvwTallyBox.y}
            width={pvwTallyBox.width}
            height={pvwTallyBox.height}
          />
        )}
        {pgmTallyBox && (
          <rect
            className="mv-tally-red"
            x={pgmTallyBox.x}
            y={pgmTallyBox.y}
            width={pgmTallyBox.width}
            height={pgmTallyBox.height}
          />
        )}

        {/* === PTZ controlling-camera framing overlay === */}
        {controllingTileBox && (
          <g aria-hidden="true">
            <rect
              x={controllingTileBox.x}
              y={controllingTileBox.y}
              width={controllingTileBox.width}
              height={controllingTileBox.height}
              fill="none"
              stroke="#ffbf47"
              strokeWidth={3}
            />
            <rect
              x={controllingTileBox.x + framing.x * controllingTileBox.width}
              y={controllingTileBox.y + framing.y * controllingTileBox.height}
              width={framing.width * controllingTileBox.width}
              height={framing.height * controllingTileBox.height}
              fill="rgba(255,191,71,0.08)"
              stroke="#ffbf47"
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
          </g>
        )}
      </svg>
    </div>
  );
}
